/**
 * CustoPorProjeto - Visão de custo por projeto dentro do Assistente de Compras.
 * Seleciona um projeto e mostra suas cotações, pedidos, total gasto, pago x em aberto
 * e o saldo comparado ao custo estimado (ficha técnica). Permite exportar em PDF.
 */
import React, { useState, useEffect, useRef } from 'react';
import api from '../../lib/axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Loader2, FolderKanban, FileText, ShoppingCart, TrendingDown, TrendingUp,
  Wallet, Download, CheckCircle2, CircleDashed,
} from 'lucide-react';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtData = (d) => { try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '—'; } };

const statusCotacaoLabel = {
  nao_solicitada: 'Rascunho', pendente: 'Em aprovação', aprovada: 'Aprovada',
  aprovada_parcial: 'Aprovada parcial', rejeitada: 'Rejeitada',
};

const STATUS_PEDIDO_OPTS = [
  { value: 'todos', label: 'Todos os status' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'recebido_parcial', label: 'Recebido parcial' },
  { value: 'recebido', label: 'Recebido' },
];

const CustoPorProjeto = ({ focusProjetoId }) => {
  const [lista, setLista] = useState([]);
  const [loadingLista, setLoadingLista] = useState(true);
  const [projetoId, setProjetoId] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [exportando, setExportando] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let ativo = true;
    setLoadingLista(true);
    api.get('/compras/assistente/custo-projetos')
      .then((r) => { if (ativo) setLista(r.data?.projetos || []); })
      .catch(() => { if (ativo) setLista([]); })
      .finally(() => { if (ativo) setLoadingLista(false); });
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    if (!projetoId) { setDetalhe(null); return; }
    let ativo = true;
    setLoadingDetalhe(true);
    setStatusFiltro('todos');
    api.get(`/compras/assistente/custo-projetos/${projetoId}`)
      .then((r) => { if (ativo) setDetalhe(r.data); })
      .catch(() => { if (ativo) setDetalhe(null); })
      .finally(() => { if (ativo) setLoadingDetalhe(false); });
    return () => { ativo = false; };
  }, [projetoId]);

  // Foco externo (alerta de estouro no Assistente)
  useEffect(() => {
    if (focusProjetoId) {
      setProjetoId(focusProjetoId);
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [focusProjetoId]);

  const handleExportarPdf = async () => {
    if (!projetoId) return;
    setExportando(true);
    try {
      const resp = await api.get(`/compras/assistente/custo-projetos/${projetoId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `custo_projeto_${(detalhe?.projeto_nome || 'projeto').replace(/[^\w]+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erro ao gerar o PDF do custo do projeto');
    } finally {
      setExportando(false);
    }
  };

  const pedidosFiltrados = (detalhe?.pedidos || []).filter(
    (p) => statusFiltro === 'todos' || p.status === statusFiltro
  );

  return (
    <div className="space-y-4" data-testid="custo-por-projeto" ref={rootRef}>
      <div className="flex items-center gap-2.5">
        <span className="p-2 rounded-xl bg-emerald-100 text-emerald-700">
          <Wallet className="w-5 h-5" />
        </span>
        <div>
          <h3 className="text-base font-semibold text-slate-900">Custo por Projeto</h3>
          <p className="text-xs text-slate-500">
            Cotações, pedidos, pago × em aberto e o total gasto de cada projeto, comparado ao custo estimado da ficha.
          </p>
        </div>
      </div>

      {/* Seletor de projeto + filtro de status + exportar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FolderKanban className="w-4 h-4 text-slate-400" />
          <Select value={projetoId} onValueChange={setProjetoId} disabled={loadingLista}>
            <SelectTrigger className="w-[340px]" data-testid="custo-projeto-select">
              <SelectValue placeholder={loadingLista ? 'Carregando projetos...' : 'Selecione um projeto'} />
            </SelectTrigger>
            <SelectContent>
              {lista.map((p) => (
                <SelectItem key={p.projeto_id} value={p.projeto_id}>
                  {p.projeto_nome}{p.cliente ? ` — ${p.cliente}` : ''} · {fmtBRL(p.total_gasto)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {detalhe && (
          <>
            <Select value={statusFiltro} onValueChange={setStatusFiltro}>
              <SelectTrigger className="w-[190px]" data-testid="custo-projeto-status-filtro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_PEDIDO_OPTS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExportarPdf} disabled={exportando}
              data-testid="custo-projeto-exportar-pdf">
              {exportando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Exportar PDF
            </Button>
          </>
        )}
      </div>

      {!projetoId && !loadingLista && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-slate-500">
            {lista.length === 0
              ? 'Nenhum projeto com atividade de compras ou custo estimado.'
              : 'Selecione um projeto acima para ver o detalhamento de custo.'}
          </CardContent>
        </Card>
      )}

      {loadingDetalhe && (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      )}

      {detalhe && !loadingDetalhe && (
        <div className="space-y-4" data-testid="custo-projeto-detalhe">
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="border-indigo-200">
              <CardContent className="py-3">
                <p className="text-[11px] text-slate-500">Custo estimado</p>
                <p className="text-lg font-bold text-indigo-700" data-testid="custo-estimado">{fmtBRL(detalhe.custo_estimado)}</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardContent className="py-3">
                <p className="text-[11px] text-slate-500">Total gasto</p>
                <p className="text-lg font-bold text-amber-700" data-testid="custo-gasto">{fmtBRL(detalhe.total_gasto)}</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200">
              <CardContent className="py-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Pago</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="custo-pago">{fmtBRL(detalhe.total_pago)}</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200">
              <CardContent className="py-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><CircleDashed className="w-3 h-3 text-orange-600" /> Em aberto</p>
                <p className="text-lg font-bold text-orange-700" data-testid="custo-aberto">{fmtBRL(detalhe.total_aberto)}</p>
              </CardContent>
            </Card>
            <Card className={detalhe.saldo >= 0 ? 'border-emerald-200' : 'border-rose-200'}>
              <CardContent className="py-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  {detalhe.saldo >= 0 ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <TrendingDown className="w-3 h-3 text-rose-600" />}
                  Saldo
                </p>
                <p className={`text-lg font-bold ${detalhe.saldo >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="custo-saldo">
                  {fmtBRL(detalhe.saldo)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Pedidos */}
          <div>
            <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2 mb-2">
              <ShoppingCart className="w-4 h-4" /> Pedidos ({pedidosFiltrados.length}{statusFiltro !== 'todos' ? ` de ${detalhe.pedidos?.length || 0}` : ''})
            </h4>
            {pedidosFiltrados.length === 0 ? (
              <p className="text-sm text-slate-400">Nenhum pedido {statusFiltro !== 'todos' ? 'neste status' : 'vinculado a este projeto'}.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2.5">Número</th>
                      <th className="text-left p-2.5">Fornecedor</th>
                      <th className="text-center p-2.5">Status</th>
                      <th className="text-right p-2.5">Total</th>
                      <th className="text-right p-2.5">Pago</th>
                      <th className="text-right p-2.5">Em aberto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidosFiltrados.map((p) => (
                      <tr key={p.id} className="border-t" data-testid={`custo-pedido-${p.numero}`}>
                        <td className="p-2.5 font-mono text-purple-600">{p.numero}</td>
                        <td className="p-2.5">{p.fornecedor_nome}<span className="block text-[11px] text-slate-400">{fmtData(p.created_at)}</span></td>
                        <td className="p-2.5 text-center"><Badge variant="outline" className="text-xs capitalize">{p.status}</Badge></td>
                        <td className="p-2.5 text-right font-medium">
                          {p.moeda !== 'BRL'
                            ? <span>{p.total.toLocaleString('pt-BR')} {p.moeda}<span className="block text-[11px] text-slate-400">≈ {fmtBRL(p.total_brl)}</span></span>
                            : fmtBRL(p.total_brl)}
                        </td>
                        <td className="p-2.5 text-right text-emerald-700">{fmtBRL(p.pago_brl)}</td>
                        <td className="p-2.5 text-right text-orange-700">{fmtBRL(p.aberto_brl)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr className="border-t-2">
                      <td colSpan={3} className="p-2.5 text-right">Totais:</td>
                      <td className="p-2.5 text-right text-amber-700">{fmtBRL(pedidosFiltrados.reduce((s, p) => s + (p.total_brl || 0), 0))}</td>
                      <td className="p-2.5 text-right text-emerald-700">{fmtBRL(pedidosFiltrados.reduce((s, p) => s + (p.pago_brl || 0), 0))}</td>
                      <td className="p-2.5 text-right text-orange-700">{fmtBRL(pedidosFiltrados.reduce((s, p) => s + (p.aberto_brl || 0), 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Cotações */}
          <div>
            <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4" /> Cotações ({detalhe.cotacoes?.length || 0})
            </h4>
            {detalhe.cotacoes?.length === 0 ? (
              <p className="text-sm text-slate-400">Nenhuma cotação vinculada a este projeto.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2.5">Número</th>
                      <th className="text-left p-2.5">Título</th>
                      <th className="text-center p-2.5">Status</th>
                      <th className="text-right p-2.5">Valor referência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.cotacoes.map((c) => (
                      <tr key={c.id} className="border-t" data-testid={`custo-cotacao-${c.numero}`}>
                        <td className="p-2.5 font-mono text-blue-600">{c.numero}</td>
                        <td className="p-2.5">{c.titulo || <span className="text-slate-400 italic">sem título</span>}<span className="block text-[11px] text-slate-400">{fmtData(c.created_at)}</span></td>
                        <td className="p-2.5 text-center"><Badge variant="outline" className="text-xs">{statusCotacaoLabel[c.status] || c.status}</Badge></td>
                        <td className="p-2.5 text-right font-medium">{c.valor > 0 ? fmtBRL(c.valor) : <span className="text-slate-400">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustoPorProjeto;
